"use client"

import { useState } from "react"
import Link from "next/link"
import { TriangleAlert } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Label } from "@/components/ui/label"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"
import {
  SMALL_SAMPLE_CONVERSIONS,
  conversionRate,
  costPerConversion,
  wilsonInterval,
} from "./conversion-rate-stats"

/**
 * Conversion rate = conversions ÷ clicks or sessions, with the denominator
 * chosen explicitly because the two give different numbers for the same
 * campaign. Spend is optional; when present it adds cost per conversion.
 *
 * Defaults are arithmetic, not benchmarks: 96 conversions from 2,400 clicks
 * = 4%, 95% range 3.29%–4.86%; $4,800 of spend → $50 per conversion.
 */

type Denominator = "clicks" | "sessions"

const DENOMINATORS: Record<
  Denominator,
  { toggle: string; label: string; noun: string; formula: string }
> = {
  clicks: {
    toggle: "Ad clicks",
    label: "Ad clicks",
    noun: "clicks",
    formula:
      "Conversion rate = Conversions ÷ Ad clicks × 100 — the ad-platform definition, which is what cost per click and cost per conversion are built on.",
  },
  sessions: {
    toggle: "Sessions",
    label: "Sessions",
    noun: "sessions",
    formula:
      "Conversion rate = Converting sessions ÷ Sessions × 100 — the analytics definition (GA4 calls it session key event rate). Count sessions with a conversion, not conversion events, so the rate cannot pass 100%.",
  },
}

const pct = (fraction: number, digits = 2) => {
  if (!Number.isFinite(fraction)) return "—"
  const percent = fraction * 100
  // Keep tiny non-zero rates visible: 1 of 100,000 is 0.001%, not 0%.
  const options =
    percent > 0 && percent < 0.5 * 10 ** -digits
      ? { maximumSignificantDigits: 2 }
      : { maximumFractionDigits: digits }
  return `${percent.toLocaleString("en-US", options)}%`
}

const formatCount = (n: number) => Math.round(n).toLocaleString("en-US")

export function ConversionRateWidget() {
  const [denominator, setDenominator] = useState<Denominator>("clicks")
  const [conversions, setConversions] = useState("96")
  const [visits, setVisits] = useState("2400")
  const [spend, setSpend] = useState("4800")

  const d = DENOMINATORS[denominator]
  const conv = parseNum(conversions)
  const den = parseNum(visits)
  const cost = parseNum(spend)

  const rate = conv === null || den === null ? NaN : conversionRate(conv, den)
  const ok = Number.isFinite(rate)
  const overHundred = ok && conv !== null && den !== null && conv > den
  const range =
    ok && !overHundred && conv !== null && den !== null
      ? wilsonInterval(conv, den)
      : null
  // Warn only when the interval is actually wide: under 30 conversions AND the
  // range spans 2x or more. 29 of 30 sessions has a tight range and is not flagged.
  const smallSample =
    ok &&
    !overHundred &&
    conv !== null &&
    conv < SMALL_SAMPLE_CONVERSIONS &&
    range !== null &&
    (range.low === 0 || range.high / range.low >= 2)
  const cpa =
    cost === null || conv === null ? NaN : costPerConversion(cost, conv)

  const rateCaption = (() => {
    if (!ok || conv === null || den === null) {
      return `Enter conversions and a ${d.noun} count above zero.`
    }
    if (overHundred) {
      return `More conversions than ${d.noun} — see the note below before using this number.`
    }
    if (conv === 0) {
      return range
        ? `No conversions yet from ${formatCount(den)} ${d.noun}. The true rate could still be as high as ${pct(range.high)}.`
        : `No conversions yet from ${formatCount(den)} ${d.noun}.`
    }
    const oneIn = `About 1 in ${(den / conv).toLocaleString("en-US", { maximumFractionDigits: 1 })} ${d.noun} converted.`
    return range
      ? `${oneIn} 95% range: ${pct(range.low)} to ${pct(range.high)}.`
      : oneIn
  })()

  const cpaCaption = (() => {
    if (cost === null) return "Add ad spend to see what each conversion cost."
    if (!Number.isFinite(cpa)) {
      return "Cost per conversion needs at least one conversion."
    }
    if (denominator === "clicks") {
      return den !== null && den > 0 && conv !== null
        ? `${formatCurrency(cost)} ÷ ${formatCount(conv)} conversions. Same as CPC (${formatCurrency(cost / den)}) ÷ conversion rate.`
        : `${formatCurrency(cost)} ÷ ${formatCount(conv ?? 0)} conversions.`
    }
    return `${formatCurrency(cost)} ÷ ${formatCount(conv ?? 0)} conversions. Spend only covers paid sessions — exclude organic traffic from both counts first.`
  })()

  return (
    <CalculatorCard title="Conversion Rate Calculator">
      <div className="mb-6 flex flex-col gap-2">
        <Label id="cvr-denominator" className="text-sm font-medium text-foreground">
          Divide conversions by
        </Label>
        <ToggleGroup
          type="single"
          variant="outline"
          aria-labelledby="cvr-denominator"
          value={denominator}
          onValueChange={(v) => {
            if (v) setDenominator(v as Denominator)
          }}
          className="justify-start"
        >
          {(Object.keys(DENOMINATORS) as Denominator[]).map((key) => (
            <ToggleGroupItem key={key} value={key} className="px-4">
              {DENOMINATORS[key].toggle}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <CalculatorGrid>
        <NumberField
          id="cvr-conversions"
          label="Conversions"
          value={conversions}
          onChange={setConversions}
          step={1}
        />
        <NumberField
          id="cvr-denominator-count"
          label={d.label}
          value={visits}
          onChange={setVisits}
          step={1}
        />
        <NumberField
          id="cvr-spend"
          label="Ad spend (optional)"
          value={spend}
          onChange={setSpend}
          prefix="$"
          hint="Leave blank if you only need the rate."
        />
      </CalculatorGrid>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <ResultStat
          label="Conversion rate"
          value={ok ? pct(rate) : "—"}
          tone={overHundred ? "bad" : smallSample ? "warn" : "neutral"}
          caption={rateCaption}
        />
        <ResultStat
          label="Cost per conversion"
          value={Number.isFinite(cpa) ? formatCurrency(cpa) : "—"}
          caption={cpaCaption}
        />
      </div>

      {overHundred ? (
        <Alert className="mt-6">
          <TriangleAlert />
          <AlertTitle>Conversions exceed {d.noun}</AlertTitle>
          <AlertDescription>
            A rate above 100% is possible in an ad platform when one click can be
            credited with several conversions — Google Ads does this when a
            conversion action counts &ldquo;Every&rdquo; conversion or when several
            actions are tracked. It also happens when the two counts come from
            different date ranges or tools. For a share-of-visitors rate, count
            one conversion per {denominator === "clicks" ? "click" : "session"}.
          </AlertDescription>
        </Alert>
      ) : null}

      {smallSample ? (
        <Alert className="mt-6">
          <TriangleAlert />
          <AlertTitle>Too few conversions to rank anything yet</AlertTitle>
          <AlertDescription>
            <p>
              With fewer than {SMALL_SAMPLE_CONVERSIONS} conversions the true rate
              could plausibly sit anywhere in the 95% range above — here a span of
              two times or more. Before calling one ad or page better than another,
              run both through the{" "}
              <Link
                href="/tools/ab-test-significance-calculator"
                className="underline underline-offset-4"
              >
                A/B test significance calculator
              </Link>
              .
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      <FormulaNote>{d.formula}</FormulaNote>
    </CalculatorCard>
  )
}
